/**
 * Recovery partagée pour les chunks périmés après un déploiement.
 *
 * Un onglet resté ouvert sur une build précédente demande des chunks dont le hash
 * n'existe plus. Deux formes d'échec :
 *   1. l'import REJETTE → Vite émet `vite:preloadError`, géré par ChunkReloadHandler.
 *   2. l'import RÉSOUT vers `undefined` / du HTML (fallback SPA sur un hash absent) :
 *      aucun preloadError, et le déréférencement de l'export plante
 *      (`Cannot read properties of undefined`).
 *
 * `withStaleChunkRecovery` couvre la forme #2 : un rechargement unique, protégé
 * contre la boucle, transforme le crash en rafraîchissement transparent.
 *
 * La clé de garde est partagée avec src/ChunkReloadHandler.ts pour que les deux
 * chemins de recovery ne se déclenchent pas en cascade.
 */
const RELOAD_GUARD_KEY = 'idelink:chunk-reload-at';
const RELOAD_GUARD_WINDOW_MS = 10_000;

/**
 * Recharge l'onglet une seule fois. Renvoie true si un rechargement a été déclenché.
 */
export const reloadOnceForStaleChunk = (): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}

	let lastReloadAt = 0;
	try {
		lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
	} catch {
		// sessionStorage bloqué (navigation privée) — on recharge quand même.
	}

	// Rechargement très récent → le chunk est réellement absent (hors ligne, panne CDN)
	// et pas seulement périmé. On laisse l'erreur remonter à l'ErrorBoundary.
	if (Date.now() - lastReloadAt < RELOAD_GUARD_WINDOW_MS) {
		return false;
	}

	try {
		sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
	} catch {
		// Stockage indisponible — on recharge quand même.
	}

	window.location.reload();
	return true;
};

/**
 * Enveloppe un import dynamique : si le module résolu est inutilisable (build
 * périmée), déclenche le rechargement unique. Pendant que le rechargement est en
 * vol, on renvoie une promesse qui ne se résout jamais pour que le loader garde
 * son fallback au lieu d'afficher l'écran d'erreur avant la navigation.
 */
export const withStaleChunkRecovery =
	<TArgs extends unknown[], TModule>(
		chunkImport: (...args: TArgs) => Promise<TModule>,
		isUsable: (module: TModule) => boolean = (module) => module !== undefined && module !== null,
	) =>
	async (...args: TArgs): Promise<TModule> => {
		try {
			const module = await chunkImport(...args);
			if (!isUsable(module)) {
				throw new Error('Lazy chunk resolved to an unusable module (stale build).');
			}
			return module;
		} catch (error) {
			if (reloadOnceForStaleChunk()) {
				return new Promise<TModule>(() => {});
			}
			throw error;
		}
	};

/** Un module chargé par React.lazy doit exposer un export `default`. */
export const hasDefaultExport = (module: unknown): boolean =>
	module !== undefined && module !== null && (module as { default?: unknown }).default != null;
